import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {usePwaInstallBanner} from './usePwaInstallBanner';

/**
 * The props for the `PwaInstallBanner` component.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'PwaInstallBanner'
     */
    testId?: string;
}

/**
 * A fixed-position banner shown when the browser reports the app as installable as a PWA.
 * Lets the user trigger the native install prompt directly, or dismiss the banner for good.
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'PwaInstallBanner'`.
 * @returns A JSX element rendering the PwaInstallBanner, or null when not applicable.
 *
 * @example
 * ```tsx
 * <PwaInstallBanner />
 * ```
 */
const PwaInstallBanner = ({testId = 'PwaInstallBanner'}: ComponentProps) => {
    const {handleDismiss, handleInstall, isOpen, t} = usePwaInstallBanner();

    if (!isOpen) return null;

    return (
        <Banner data-cy={testId}>
            <BannerText>{t('common.installApp')}</BannerText>
            <Actions>
                <DismissButton data-cy={`${testId}-dismiss`} type="button" onClick={handleDismiss}>
                    {t('common.notNow')}
                </DismissButton>
                <InstallButton data-cy={`${testId}-install`} type="button" onClick={handleInstall}>
                    {t('common.installNow')}
                </InstallButton>
            </Actions>
        </Banner>
    );
};

PwaInstallBanner.displayName = 'PwaInstallBanner';

export {PwaInstallBanner};

const Banner = styled.div`
    align-items: center;
    background: ${({theme}) => theme.colors.primaryDefault};
    border-radius: 1.2rem;
    bottom: ${spacing(4)};
    box-shadow: ${({theme}) => theme.boxShadows.card};
    display: flex;
    gap: ${spacing(4)};
    justify-content: space-between;
    left: ${spacing(4)};
    padding: ${spacing(3)} ${spacing(4)};
    position: fixed;
    right: ${spacing(4)};
    z-index: 9999;
`;

const BannerText = styled.span`
    color: ${({theme}) => theme.colors.white};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 500;
`;

const Actions = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: ${spacing(2)};
`;

const DismissButton = styled.button`
    background: transparent;
    border: none;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
    font-weight: 500;
    padding: ${spacing(2)} ${spacing(4)};
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }
`;

const InstallButton = styled.button`
    background: ${({theme}) => theme.colors.accentWarm};
    border: none;
    border-radius: 0.8rem;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
    font-weight: 600;
    padding: ${spacing(2)} ${spacing(4)};
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }
`;